<template>
  <section class="activity-section activity-task-progress" :class="{'is-package':useNewbiePanel}" :aria-label="useNewbiePanel?'新人礼包':'本人任务进度'">
    <header v-if="!useNewbiePanel"><h3>本人任务进度</h3><button v-if="isLoggedIn" type="button" :disabled="loading || submitting || packageBusy" @click="refresh">刷新进度</button></header>
    <template v-if="!isLoggedIn"><p>登录后查看任务进度和领取状态。</p><button class="primary" type="button" @click="openAuth('login')">登录后查看任务进度</button><ActivityNewcomer v-if="publicNewcomer" :model="publicNewcomer" busy="read"/></template>
    <p v-else-if="loading" role="status">正在查询任务进度…</p>
    <div v-else-if="error" role="alert"><p>{{ error }}</p><button type="button" :disabled="submitting" @click="refresh">重新查询进度</button><ActivityNewcomer v-if="publicNewcomer" :model="publicNewcomer" busy="read"/></div>
    <ActivityCampaignPanel v-else-if="useNewbiePanel" :activity="activity" @busy="packageBusy=$event;emit('busy', $event)" />
    <template v-else-if="state">
      <p v-if="activity.activityType === 'NEWBIE_TASK'">注册后 {{ state.rules.newbieDays }} 天内完成任务，是否可领以实际核验结果为准。</p>
      <p v-else>{{ state.today }} · 连续达标 {{ state.streakDays }} 天 · {{ state.qualifiedToday ? '今日已达标' : '今日未达标' }}；每日到账门槛 {{ state.rules.dailyDepositAmount }} 元。</p>
      <p v-if="!rows.length">当前暂无任务规则。</p>
      <article v-for="row in rows" :key="row.key" class="activity-task-row">
        <div><h4>{{ row.title }}</h4><p v-if="row.targetAmount">到账进度 {{ row.currentAmount || '0.00' }} / {{ row.targetAmount }} 元</p><p>奖励 {{ row.rewardAmount }} 元</p><span>{{ row.claimed ? '已领取' : row.completed ? '已达标' : '未达标' }}</span></div>
        <button v-if="canClaim(row)" type="button" :disabled="submitting" @click="prepare(row)">领取奖励</button>
        <button v-else-if="!row.completed && !row.claimed && taskRoute(row)" type="button" :disabled="submitting" @click="navigate(taskRoute(row))">{{ taskRoute(row) === '/mine/finance/deposit' ? '去存款' : '去完善资料' }}</button>
      </article>
    </template>
    <p v-if="claimError" role="alert">{{ claimError }}</p><p v-if="success" role="status">{{ success }}</p>
    <form v-if="confirmation" class="activity-task-confirm" aria-label="确认任务奖励领取" @submit.prevent="claim">
      <h4>确认领取 {{ confirmation.title }}</h4><p>预计奖励 {{ confirmation.rewardAmount }} 元，实际到账以服务端核验结果为准。</p>
      <button class="primary" :disabled="submitting">{{ submitting ? '核验中…' : '确认领取' }}</button><button type="button" :disabled="submitting" @click="confirmation = null">取消</button>
    </form>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useAppContext } from '../composables/appContext'
import { authStorage } from '../services/http'
import { webApi } from '../services/webApi'
import { isTaskActivity } from '../services/activityTasks'
import ActivityCampaignPanel from './activities/ActivityCampaignPanel.vue'
import ActivityNewcomer from './activities/ActivityNewcomer.vue'
import {publicNewcomerView} from '../services/activities/activityPublicView.mjs'

const props = defineProps({ activity: { type: Object, required: true } })
const emit = defineEmits(['busy'])
const { isLoggedIn, openAuth, navigate, loadMember } = useAppContext()
const state = ref(null), loading = ref(false), error = ref(''), submitting = ref(false)
const packageBusy = ref(false)
const confirmation = ref(null), claimError = ref(''), success = ref('')
let revision = 0, actionRevision = 0
const packageInfo = computed(() => state.value?.newbiePackage || {})
const useNewbiePanel = computed(() => packageInfo.value.enabled === true || Array.isArray(state.value?.newbieGifts) && state.value.newbieGifts.length > 0)
const publicNewcomer = computed(() => props.activity.activityType === 'NEWBIE_TASK' ? publicNewcomerView(props.activity,error.value) : null)
const rows = computed(() => {
  if (!state.value) return []
  const rules = state.value.rules
  const definitions = props.activity.activityType === 'NEWBIE_TASK' ? rules.tasks || [] : (rules.milestones || []).map(item => ({
    key: String(item.days), title: `连续达标 ${item.days} 天奖励`, rewardAmount: item.rewardAmount,
  }))
  return definitions.map(item => ({ ...item, ...(state.value.progress.find(progress => progress.key === item.key) || {}) }))
})

// 先清除旧状态，失败时关闭领取，不能把查不到进度当作尚未领取。
async function readState() {
  const current = ++revision, token = authStorage.getToken(), id = props.activity.id, type = props.activity.activityType
  state.value = null; error.value = ''; confirmation.value = null; loading.value = false
  if (!isLoggedIn.value || !token || !isTaskActivity(props.activity)) return false
  loading.value = true
  try {
    // 整体礼包优先读取永久原凭证；当前活动过期、停用或改规则不能遮住已领取结果。
    const result = await (type === 'NEWBIE_TASK' ? webApi.newbiePackage(id) : webApi.gameplayState(id))
    if (current !== revision || token !== authStorage.getToken() || !isLoggedIn.value) return false
    if (String(result?.activityId) !== String(id) || result.activityType !== type || !result.rules || !Array.isArray(result.progress)) throw new Error('活动进度数据不匹配，请重新查询')
    state.value = result
    return true
  } catch (failure) { if (current === revision && token === authStorage.getToken()) error.value = failure.message || '任务进度查询失败' }
  finally { if (current === revision) loading.value = false }
  return false
}
async function refresh() {
  if (submitting.value || packageBusy.value) return
  success.value = ''; claimError.value = ''
  await readState()
}
function canClaim(row) {
  return Boolean(state.value && !loading.value && !error.value && !useNewbiePanel.value && row.completed === true && row.claimed === false)
}
function taskRoute(row) {
  if (props.activity.activityType === 'DEPOSIT_STREAK') return '/mine/finance/deposit'
  return { BIND_PHONE: '/member', BIND_BANK_CARD: '/mine/finance/wallet', PROFILE: '/member', FIRST_DEPOSIT: '/mine/finance/deposit', TOTAL_DEPOSIT: '/mine/finance/deposit' }[row.type] || ''
}
function prepare(row) {
  if (submitting.value || !canClaim(row)) return
  claimError.value = ''; success.value = ''; confirmation.value = { ...row }
}

// 任务/里程碑由服务端永久防重；超时只读核对，不自动重发领取。
async function claim() {
  if (submitting.value || loading.value || !confirmation.value || !state.value || !isLoggedIn.value) return
  const original = confirmation.value, current = revision, operation = ++actionRevision, token = authStorage.getToken(), id = props.activity.id
  if (!token) return openAuth('login')
  if (!canClaim(rows.value.find(item => item.key === original.key) || {})) return
  const payload = { action: 'CLAIM', targetKey: original.key }
  submitting.value = true; emit('busy', true); claimError.value = ''; success.value = ''
  try {
    const result = await webApi.gameplayAction(id, payload)
    if (current !== revision || token !== authStorage.getToken() || !isLoggedIn.value) return
    if (!result?.recordId || result.status !== 'COMPLETED' || !(Number(result.rewardAmount) > 0)) throw new Error('领取结果待核对，请刷新进度')
    confirmation.value = null
    success.value = `${result.rewardAmount} 元已到账`
    await readState()
    if (token === authStorage.getToken() && isLoggedIn.value && String(id) === String(props.activity.id)) await loadMember?.()
  } catch (failure) {
    if (current !== revision || token !== authStorage.getToken() || !isLoggedIn.value) return
    confirmation.value = null
    claimError.value = `领取结果待核对：${failure.message || '请求未完成'}。请查询进度，勿重复领取。`
    const checked = await readState()
    if (token !== authStorage.getToken() || !isLoggedIn.value || String(id) !== String(props.activity.id)) return
    const claimed = rows.value.find(item => item.key === original.key)?.claimed
    if (checked && claimed) { claimError.value = ''; success.value = '已核对该奖励已领取'; await loadMember?.() }
  } finally {
    if (operation === actionRevision) { submitting.value = false; emit('busy', false) }
  }
}
watch(() => [props.activity.id, props.activity.activityType, isLoggedIn.value], () => {
  actionRevision++; success.value = ''; claimError.value = ''; submitting.value = false; emit('busy', false); void readState()
}, { immediate: true })
onBeforeUnmount(() => { revision++; actionRevision++; emit('busy', false) })
</script>

<style scoped>
.activity-task-progress { padding: 20px; margin-top: 16px; border: 1px solid #e5ebf2; border-radius: 12px; }
.activity-task-progress.is-package { padding: 0; border: 0; }
.activity-task-progress header, .activity-task-row { display: flex; justify-content: space-between; align-items: center; gap: 16px; }
.activity-task-progress h3 { margin: 0; font-size: 17px; }
.activity-task-progress h4 { margin: 0 0 8px; font-size: 15px; }
.activity-task-progress p { line-height: 1.7; color: #617187; overflow-wrap: anywhere; }
.activity-task-row { margin-top: 12px; padding: 16px; background: #f4f7fa; border-radius: 10px; }
.activity-task-row > div { min-width: 0; }
.activity-task-row p { margin: 4px 0; }
.activity-task-row span { color: #35668a; font-size: 13px; }
.activity-task-progress button { flex: none; min-height: 38px; padding: 9px 16px; border: 1px solid #dfe7f0; border-radius: 8px; background: #f5f7fa; color: #40536b; font: inherit; font-size: 14px; }
.activity-task-progress button.primary { color: white; background: #1476d4; }
.activity-task-progress button:disabled { opacity: .5; cursor: not-allowed; }
.activity-task-progress button:focus-visible { outline: 2px solid #1476d4; outline-offset: 3px; }
.activity-task-confirm { margin-top: 16px; padding: 16px; border: 1px solid #dfe7f0; border-radius: 8px; }
.activity-task-confirm button + button { margin-left: 8px; }
.activity-task-progress [role="alert"] { color: #b42318; }
@media (max-width: 600px) { .activity-task-progress { padding: 16px; } .activity-task-row { flex-wrap: wrap; } }
</style>
